import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Loader2, Save, Sparkles, Type, Hash, MessageCircle,
  Smile, Zap, PartyPopper, X
} from 'lucide-react';
import { toast } from 'sonner';
import './CaptionStyleEditor.css';

const API = process.env.REACT_APP_BACKEND_URL;

const EMOJI_LEVELS = [
  { key: 'none', label: 'Clean', desc: 'No emojis — pure text', icon: Type },
  { key: 'light', label: 'Subtle', desc: 'One or two, tasteful', icon: Smile },
  { key: 'regular', label: 'Expressive', desc: 'Natural use throughout', icon: Sparkles },
  { key: 'heavy', label: 'Loaded', desc: 'Emojis in every sentence', icon: PartyPopper },
];

const TONE_OPTIONS = [
  { key: 'professional', label: 'Professional', color: '#3b82f6', desc: 'Corporate, polished' },
  { key: 'friendly', label: 'Warm & Friendly', color: '#f59e0b', desc: 'Approachable, relaxed' },
  { key: 'luxury', label: 'Luxury', color: '#a78bfa', desc: 'Elegant, premium feel' },
  { key: 'playful', label: 'Playful', color: '#22c55e', desc: 'Fun, energetic' },
  { key: 'direct', label: 'Straight-Talking', color: '#ef4444', desc: 'No fluff, to the point' },
  { key: 'local', label: 'Local Expert', color: '#06b6d4', desc: 'Community-focused, knowledgeable' },
];

const SPACING_OPTIONS = [
  { key: 'compact', label: 'Compact', desc: 'Dense paragraph' },
  { key: 'spaced', label: 'Spaced', desc: 'Line breaks between ideas' },
  { key: 'airy', label: 'Airy', desc: 'Short punchy lines' },
];

export default function CaptionStyleEditor({ branchId, branchName, onClose }) {
  const [guidelines, setGuidelines] = useState({
    tone: 'friendly',
    emoji_level: 'light',
    spacing: 'spaced',
    sign_off: '',
    hashtags: '',
    hook_line: '',
    use_hook: true,
    custom_instructions: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewCaption, setPreviewCaption] = useState('');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/social-week/scheduler/caption-guidelines/${branchId}`)
      .then(res => {
        const d = res.data;
        setGuidelines(prev => ({
          ...prev,
          tone: d.tone || 'friendly',
          emoji_level: d.emoji_level || 'light',
          spacing: d.spacing || 'spaced',
          sign_off: d.sign_off || '',
          hashtags: d.hashtags || '',
          hook_line: d.hook_line || '',
          use_hook: d.use_hook !== false,
          custom_instructions: d.custom_instructions || '',
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [branchId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/social-week/scheduler/caption-guidelines/${branchId}`, guidelines);
      toast.success('Caption style saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/api/social-week/scheduler/generate-caption`, {
        template_id: 'just_listed',
        listing_address: '14 Maple Drive, Cobham, Surrey KT11',
        listing_price: '£825,000',
        listing_features: ['4 bedrooms', 'South-facing garden', 'Recently renovated kitchen'],
        branch_id: branchId,
      });
      setPreviewCaption(res.data.caption);
    } catch {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="cse-wrap" data-testid="caption-style-editor">
        <div className="cse-loading"><Loader2 size={20} className="spin" /></div>
      </div>
    );
  }

  const selectedTone = TONE_OPTIONS.find(t => t.key === guidelines.tone) || TONE_OPTIONS[1];

  return (
    <div className="cse-wrap" data-testid="caption-style-editor">
      <div className="cse-header">
        <div className="cse-header-left">
          <MessageCircle size={18} />
          <div>
            <h2 className="cse-title">Social Voice</h2>
            <span className="cse-subtitle">{branchName}</span>
          </div>
        </div>
        {onClose && (
          <button className="cse-close" onClick={onClose} data-testid="cse-close"><X size={18} /></button>
        )}
      </div>

      {/* Tone Picker */}
      <div className="cse-section">
        <h3 className="cse-section-title">Voice & Tone</h3>
        <div className="cse-tone-grid">
          {TONE_OPTIONS.map(t => (
            <button
              key={t.key}
              className={`cse-tone-card ${guidelines.tone === t.key ? 'cse-tone-active' : ''}`}
              style={{ '--tone-color': t.color }}
              onClick={() => setGuidelines(g => ({ ...g, tone: t.key }))}
              data-testid={`cse-tone-${t.key}`}
            >
              <span className="cse-tone-dot" />
              <span className="cse-tone-label">{t.label}</span>
              <span className="cse-tone-desc">{t.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Emoji Level */}
      <div className="cse-section">
        <h3 className="cse-section-title">Emoji Usage</h3>
        <div className="cse-emoji-track">
          {EMOJI_LEVELS.map((e, i) => {
            const isActive = guidelines.emoji_level === e.key;
            const Icon = e.icon;
            return (
              <button
                key={e.key}
                className={`cse-emoji-option ${isActive ? 'cse-emoji-active' : ''}`}
                onClick={() => setGuidelines(g => ({ ...g, emoji_level: e.key }))}
                data-testid={`cse-emoji-${e.key}`}
              >
                <Icon size={16} />
                <span className="cse-emoji-label">{e.label}</span>
                <span className="cse-emoji-desc">{e.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Spacing */}
      <div className="cse-section">
        <h3 className="cse-section-title">Post Layout</h3>
        <div className="cse-spacing-row">
          {SPACING_OPTIONS.map(s => (
            <button
              key={s.key}
              className={`cse-spacing-btn ${guidelines.spacing === s.key ? 'cse-spacing-active' : ''}`}
              onClick={() => setGuidelines(g => ({ ...g, spacing: s.key }))}
              data-testid={`cse-spacing-${s.key}`}
            >
              <div className={`cse-spacing-preview cse-spacing-${s.key}`}>
                <div /><div /><div />
              </div>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hook Line */}
      <div className="cse-section">
        <div className="cse-hook-header">
          <div>
            <h3 className="cse-section-title">Opening Hook</h3>
            <span className="cse-field-hint">AI adds a punchy attention-grabbing first line</span>
          </div>
          <label className="cse-toggle-label">
            <input
              type="checkbox"
              checked={guidelines.use_hook}
              onChange={e => setGuidelines(g => ({ ...g, use_hook: e.target.checked }))}
              className="cse-checkbox"
              data-testid="cse-hook-toggle"
            />
            <span className="cse-toggle-slider" />
          </label>
        </div>
      </div>

      {/* Sign-off & Hashtags */}
      <div className="cse-section cse-section-row">
        <div className="cse-field">
          <h3 className="cse-section-title">Sign-off</h3>
          <input
            className="cse-input"
            value={guidelines.sign_off}
            onChange={e => setGuidelines(g => ({ ...g, sign_off: e.target.value }))}
            placeholder="e.g. DM us or call 01234 567890"
            data-testid="cse-signoff"
          />
        </div>
        <div className="cse-field">
          <h3 className="cse-section-title">Default Hashtags</h3>
          <div className="cse-hashtag-wrap">
            <Hash size={13} className="cse-hashtag-icon" />
            <input
              className="cse-input cse-input-hashtag"
              value={guidelines.hashtags}
              onChange={e => setGuidelines(g => ({ ...g, hashtags: e.target.value }))}
              placeholder="NewHome Cobham EstateAgent"
              data-testid="cse-hashtags"
            />
          </div>
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="cse-section">
        <h3 className="cse-section-title">Anything else?</h3>
        <textarea
          className="cse-textarea"
          value={guidelines.custom_instructions}
          onChange={e => setGuidelines(g => ({ ...g, custom_instructions: e.target.value }))}
          placeholder="e.g. Always mention our free valuation. Never say 'property' — say 'home'."
          rows={2}
          data-testid="cse-custom"
        />
      </div>

      {/* Preview + Save */}
      <div className="cse-actions">
        <button className="cse-preview-btn" onClick={handlePreview} disabled={previewing} data-testid="cse-preview-btn">
          {previewing ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
          Preview Caption
        </button>
        <button className="cse-save-btn" onClick={handleSave} disabled={saving} data-testid="cse-save-btn">
          {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          Save Style
        </button>
      </div>

      {/* Live Preview */}
      {previewCaption && (
        <div className="cse-preview-card" data-testid="cse-preview-output">
          <div className="cse-preview-header">
            <Sparkles size={13} />
            <span>Preview — Just Listed</span>
            <span className="cse-preview-tone" style={{ color: selectedTone.color }}>{selectedTone.label}</span>
          </div>
          <div className="cse-preview-text">{previewCaption}</div>
        </div>
      )}
    </div>
  );
}
